import { TranslationNamespaces } from '~/lib/constants';
import { useMemo } from 'react';
import { P2GDashboardTabProps } from '~/components/dashboard/dash-tile/tab';
import { useTranslation } from 'react-i18next';
import { P2GTab, P2GTabs } from '@parcel2go/core-ui';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { useSmartNavigationData } from '../hooks/use-smart-navigation-data';

type props = {
  currentPageRoute: string;
  routes: string[];
};

const SmartSendNavigation = ({ currentPageRoute, routes }: props) => {
  const { soldCount, inTransitCount } = useSmartNavigationData();
  const { goTo } = useGoto();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const soldItemsTile = useMemo(() => {
    return {
      onClick: () => goTo(RoutingConstants.SmartSend),
      key: RoutingConstants.SmartSend,
      selected: currentPageRoute === RoutingConstants.SmartSend,
      title: t('ORDER_AREA_TAB_NEW_SMARTSEND_ORDERS', {
        defaultValue: 'New SmartSend Orders ({{value}})',
        value: soldCount
      }),
      type: 'NUMBER',
      value: soldCount || ('...' as any)
    };
  }, [currentPageRoute, t, goTo, soldCount]);

  const inTransitTile = useMemo(() => {
    return {
      onClick: () => goTo(RoutingConstants.InTransit),
      key: RoutingConstants.InTransit,
      selected: currentPageRoute === RoutingConstants.InTransit,
      title: t('ORDER_AREA_TAB_ORDERS_INTRANSIT', {
        defaultValue: 'Orders InTransit ({{value}})',
        value: inTransitCount
      }),
      value: inTransitCount || 0
    };
  }, [currentPageRoute, t, goTo, inTransitCount]);

  const bookedItemsTile = useMemo(() => {
    return {
      onClick: () => goTo(RoutingConstants.OrderHistory),
      key: RoutingConstants.OrderHistory,
      selected: currentPageRoute === RoutingConstants.OrderHistory,
      // Deliberately uncounted. The number was only meaningful on the dashboard tile, which
      // labelled it "Last 30 Days"; on a tab it reads as a lifetime total, which it is not.
      title: t('ORDER_AREA_TAB_ORDER_HISTORY_LABEL', { defaultValue: 'Order History' })
    };
  }, [currentPageRoute, t, goTo]);

  const archivedOrdersTile = useMemo(() => {
    return {
      onClick: () => goTo(RoutingConstants.ArchivedOrders),
      key: RoutingConstants.ArchivedOrders,
      selected: currentPageRoute === RoutingConstants.ArchivedOrders,
      title: t('ORDER_AREA_TAB_ARCHIVED_ORDERS', { defaultValue: 'Archived Orders' })
    };
  }, [currentPageRoute, t, goTo]);

  const tiles = useMemo(() => {
    return ([soldItemsTile, inTransitTile, bookedItemsTile, archivedOrdersTile] as P2GDashboardTabProps[])
      .filter((x) => routes.includes(x.key))
      .map((x, i) => ({
        ...x,
        index: i
      }));
  }, [soldItemsTile, inTransitTile, bookedItemsTile, archivedOrdersTile, routes]);

  const currentIndex = useMemo(() => {
    const index = tiles.findIndex((tile) => tile.key === currentPageRoute);
    return index >= 0 ? index : 0;
  }, [tiles, currentPageRoute]);

  return (
    <P2GTabs value={currentIndex}>
      {tiles.map((link, index) => (
        <P2GTab key={link.key} label={link.title} onClick={() => link?.onClick?.()} index={index} />
      ))}
    </P2GTabs>
  );
};

export default SmartSendNavigation;
